/**
 * New / edit hired-in equipment and fees — Board 1's third and fourth forms
 * (`catalog-units-and-buying.md` §6.1).
 *
 * The two are one form with two sets of words. Both are bought one way and
 * allocated another — a skid steer hired by the week and charged by the day,
 * a roll-off paid by the haul and filled by the yard — so both get the two
 * pairs, the green sentence, a minimum, the buying rule and Try it, exactly
 * as Materials has them. What differs is the first section (a hire shop and a
 * category for equipment, a description for a fee) and the words built from
 * the units.
 *
 * Retired and gone, not hidden (§6.2): the rental's delivery charge and the
 * fee's once-a-job switch. A delivery is a Fee.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  buyingSentence, buyingUnitWord, costPerAlloc, marginPctOf, sellsPer,
  type BuyingRule, type OrgUnitSummary,
} from '@rowted/shared'
import { Band } from '../primitives.js'
import {
  ComboInput, Field, FormGrid, FormSection, MoneyInput, NumberInput, PairField, Readout,
  SelectInput, TextInput, Wide,
} from '../form/index.js'
import { request } from '../../lib/api.js'
import { formatNumber, formatUnitCost } from '../../lib/format.js'
import {
  CatalogSheet, CustomerNameField, NotesField, PricedByField, SegmentedPicker, TryItPanel,
  UnitSelect, WhereAndOptions, amountText, marginTone, percentText, readAmount, unitNamed,
} from './catalog-parts.js'
import { createCategory, createUnit, useCatalogLookups } from './catalog-lookups.js'
import { countChanges } from './labor-form.js'
import {
  ALLOCATED_IN, BAND_HINT, BUY_LABEL, CATEGORY, DESCRIPTION, HIRED_FROM, KIND_OF_MACHINE,
  MARKUP_ON_COST, MINIMUM_HINT, MINIMUM_LABEL, NAME, OWNED_BAND_HINT, OWNED_BUY_LABEL,
  OWNED_MINIMUM_HINT, OWNED_SUB, WHAT_IT_COSTS, WHAT_IT_COSTS_SUB, WHAT_IT_IS,
  WHAT_IT_SELLS_FOR, costPerLabel, costSlash, ownedCostPerLabel, ownedMachinesHint, perLabel,
  pricePer, ruleHint, ruleLabel, ruleUsed, ruleWhole, sellsSlash,
} from './catalog-copy.js'

type Kind = 'equipment' | 'fee'

interface BuyingDraft {
  name: string
  /** Customer-facing name (0126); blank is "the same as the name". */
  customerName: string
  description: string
  vendorId: string | null
  categoryId: string | null
  purchaseUnitId: string | null
  unitId: string | null
  purchaseConversion: string
  purchasePrice: string
  minOrderQty: string
  costBasis: 'used' | 'whole'
  priceMode: 'markup' | 'fixed'
  markupPct: string
  price: string
  taxable: boolean
  orgUnitIds: string[]
  notes: string
  active: boolean
  version?: number
}

/*
 * A new one is bought whole (D3: "Hired and hauled things default to The
 * whole one"); Board 1 draws the hired form on a fixed price and the fee on
 * its own markup.
 */
const blank = (kind: Kind): BuyingDraft => ({
  name: '', customerName: '', description: '', vendorId: null, categoryId: null, purchaseUnitId: null,
  unitId: null, purchaseConversion: '1', purchasePrice: '', minOrderQty: '1',
  costBasis: 'whole', priceMode: kind === 'equipment' ? 'fixed' : 'markup', markupPct: '',
  price: '', taxable: false, orgUnitIds: [], notes: '', active: true,
})

const PATH: Record<Kind, string> = {
  equipment: '/catalog-admin/rentals', fee: '/catalog-admin/deployment-costs',
}
const NOUN: Record<Kind, string> = { equipment: 'hired-in item', fee: 'fee' }

export function BuyingForm({ kind, id, canEdit, available, onClose, onSaved }: {
  kind: Kind
  id: string | null
  canEdit: boolean
  available: OrgUnitSummary[]
  onClose: () => void
  onSaved: (message: string, again: boolean) => void
}) {
  const lookups = useCatalogLookups()
  /*
   * One of the company's own kinds of machine (0129), read off the row: the
   * same row and the same options as a hired-in one, with the type and the
   * fleet's count where the hire shop is. Null for anything hired in or new.
   */
  const [owned, setOwned] = useState<{ typeName: string; machines: number } | null>(null)
  const [draft, setDraft] = useState<BuyingDraft | null>(id ? null : blank(kind))
  const [saved, setSaved] = useState<BuyingDraft | null>(id ? null : blank(kind))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /* Try it is never saved; null follows the conversion until somebody types. */
  const [tryQty, setTryQty] = useState<string | null>(null)
  const body = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    if (!id) return
    try {
      const row = await request<Record<string, unknown>>(`${PATH[kind]}/${id}`)
      const numberText = (value: unknown) =>
        value === null || value === undefined ? '' : String(Number(value))
      const loaded: BuyingDraft = {
        name: String(row.name ?? ''),
        customerName: String(row.customerName ?? ''),
        description: String(row.description ?? ''),
        vendorId: (row.vendorId as string | null) ?? null,
        categoryId: (row.categoryId as string | null) ?? null,
        purchaseUnitId: (row.purchaseUnitId as string | null) ?? null,
        unitId: (row.unitId as string | null) ?? null,
        purchaseConversion: numberText(row.purchaseConversion) || '1',
        purchasePrice: amountText(row.purchasePrice, true),
        minOrderQty: numberText(row.minOrderQty),
        costBasis: row.costBasis === 'whole' ? 'whole' : 'used',
        priceMode: row.priceMode === 'markup' ? 'markup' : 'fixed',
        markupPct: numberText(row.markupPct),
        price: amountText(row.price, true),
        taxable: row.taxable === true,
        orgUnitIds: (row.orgUnitIds as string[] | undefined) ?? [],
        notes: String(row.notes ?? ''),
        active: row.active !== false,
        version: Number(row.version ?? 0),
      }
      setOwned(row.equipmentTypeId
        ? { typeName: String(row.equipmentTypeName ?? ''), machines: Number(row.machineCount ?? 0) }
        : null)
      setDraft(loaded)
      setSaved(loaded)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : `That ${NOUN[kind]} could not be loaded.`)
    }
  }, [id, kind])
  useEffect(() => { void load() }, [load])

  const patch = (over: Partial<BuyingDraft>) => {
    setDraft((was) => (was ? { ...was, ...over } : was))
    setErrors((was) => Object.fromEntries(Object.entries(was).filter(([key]) => !(key in over))))
  }

  const alloc = unitNamed(lookups.units, draft?.unitId ?? null, 'unit')
  const buy = unitNamed(lookups.units, draft?.purchaseUnitId ?? draft?.unitId ?? null, alloc.name)
  const per = Math.max(readAmount(draft?.purchaseConversion) ?? 1, 0) || 1
  const rule: BuyingRule = {
    buyUnit: buy.code, buyUnitName: buy.name, allocUnit: alloc.code, allocUnitName: alloc.name,
    per, buyPrice: readAmount(draft?.purchasePrice) ?? 0,
    minOrder: readAmount(draft?.minOrderQty) ?? 0, basis: draft?.costBasis ?? 'used',
  }
  const cost = costPerAlloc(rule)
  const sells = sellsPer(cost, draft?.priceMode ?? 'fixed', readAmount(draft?.price) ?? 0,
    readAmount(draft?.markupPct) ?? 0)
  const margin = marginPctOf(Math.round(sells * 1e6), Math.round(cost * 1e6))
  const allocOne = buyingUnitWord(alloc.code, alloc.name, 1)
  const allocMany = buyingUnitWord(alloc.code, alloc.name, 2)
  const buyOne = buy.name.toLowerCase()
  const autoQty = formatNumber(Math.round(per * 0.6) || 1)

  const changes = useMemo(() => countChanges(draft, saved), [draft, saved])

  async function save(again: boolean) {
    if (!draft) return
    const found: Record<string, string> = {}
    if (!draft.name.trim()) found.name = `A ${NOUN[kind]} needs a name`
    if (!draft.unitId) found.unitId = 'Say what a job is charged in'
    if ((readAmount(draft.purchaseConversion) ?? 0) <= 0) {
      found.purchaseConversion = 'One has to yield something'
    }
    setErrors(found)
    if (Object.keys(found).length > 0) {
      body.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus()
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      await request(id ? `${PATH[kind]}/${id}` : PATH[kind], {
        method: id ? 'PUT' : 'POST',
        body: {
          name: draft.name,
          customerName: draft.customerName.trim() || null,
          description: draft.description,
          ...(kind === 'equipment'
            ? { vendorId: owned ? null : draft.vendorId, categoryId: draft.categoryId } : {}),
          purchaseUnitId: draft.purchaseUnitId ?? draft.unitId,
          unitId: draft.unitId,
          purchaseConversion: per,
          purchasePrice: rule.buyPrice,
          minOrderQty: rule.minOrder,
          costBasis: draft.costBasis,
          priceMode: draft.priceMode,
          markupPct: draft.priceMode === 'markup' ? readAmount(draft.markupPct) ?? 0 : null,
          price: draft.priceMode === 'fixed' ? readAmount(draft.price) ?? 0 : sells,
          taxable: draft.taxable,
          orgUnitIds: draft.orgUnitIds,
          notes: draft.notes || null,
          active: draft.active,
          ...(id ? { version: draft.version ?? 0 } : {}),
        },
      })
      if (again) {
        const next = {
          ...blank(kind), vendorId: draft.vendorId, categoryId: draft.categoryId,
          purchaseUnitId: draft.purchaseUnitId, unitId: draft.unitId,
          purchaseConversion: draft.purchaseConversion, costBasis: draft.costBasis,
          priceMode: draft.priceMode, markupPct: draft.markupPct,
        }
        setDraft(next)
        setSaved(next)
      }
      onSaved(id ? `Saved ${draft.name}.` : `Added ${draft.name}.`, again)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  async function newUnit(name: string, key: 'purchaseUnitId' | 'unitId') {
    const unit = await createUnit(name, lookups, setProblem)
    if (unit) patch({ [key]: unit.id } as Partial<BuyingDraft>)
  }

  const errorCount = Object.keys(errors).length
  return (
    <CatalogSheet
      kind={kind}
      sub={owned ? OWNED_SUB : undefined}
      tile={kind === 'equipment' ? 'equipment' : 'deployment'}
      title={draft?.name || (id ? (kind === 'equipment' ? 'Hired-in item' : 'Fee')
        : (kind === 'equipment' ? 'New hired-in item' : 'New fee'))}
      dirty={changes > 0}
      changeCount={changes}
      busy={busy}
      canEdit={canEdit}
      isNew={!id}
      onClose={onClose}
      onSave={(again) => void save(again)}
      note={errorCount > 0
        ? (errorCount === 1 ? 'One field needs attention' : `${errorCount} fields need attention`)
        : undefined}
    >
      {!draft
        ? <p className="m-0 text-cell text-text-muted">Loading…</p>
        : (
          <div ref={body} className="flex flex-col gap-3.5">
            {problem && <Band tone="amber">{problem}</Band>}

            <FormSection title={WHAT_IT_IS} />
            <FormGrid>
              <Wide>
                <Field label={NAME} required error={errors.name}>
                  {({ id: fieldId, describedBy, invalid }) => (
                    <TextInput id={fieldId} describedBy={describedBy} invalid={invalid}
                      value={draft.name} disabled={!canEdit}
                      onChange={(event) => patch({ name: event.target.value })} />
                  )}
                </Field>
              </Wide>
              <CustomerNameField value={draft.customerName} name={draft.name} disabled={!canEdit}
                onChange={(customerName) => patch({ customerName })} />
              {kind === 'equipment'
                ? (
                  <>
                    {owned
                      ? (
                        <Field label={KIND_OF_MACHINE} hint={ownedMachinesHint(owned.machines)}>
                          {({ id: fieldId, describedBy }) => (
                            <TextInput id={fieldId} describedBy={describedBy}
                              value={owned.typeName} disabled />
                          )}
                        </Field>
                      )
                      : (
                        <Field label={HIRED_FROM}>
                          {({ id: fieldId }) => (
                            <SelectInput id={fieldId} label={HIRED_FROM} value={draft.vendorId}
                              disabled={!canEdit} clearable clearLabel="None yet"
                              placeholder="None yet"
                              onChange={(value) => patch({ vendorId: value })}
                              options={lookups.vendors.map((one) => ({ value: one.id, label: one.name }))} />
                          )}
                        </Field>
                      )}
                    <Field label={CATEGORY}>
                      {({ id: fieldId, describedBy }) => (
                        <ComboInput
                          id={fieldId}
                          describedBy={describedBy}
                          label={CATEGORY}
                          thing="category"
                          disabled={!canEdit}
                          value={draft.categoryId
                            ? {
                              id: draft.categoryId,
                              name: lookups.categories.find((one) => one.id === draft.categoryId)?.name ?? '',
                            }
                            : null}
                          onPick={(picked) => patch({ categoryId: picked.id })}
                          search={(query) => lookups.categories
                            .filter((one) => one.name.toLowerCase().includes(query.trim().toLowerCase()))
                            .slice(0, 8)}
                          onCreate={canEdit
                            ? (name) => createCategory(name, lookups, setProblem) : undefined}
                        />
                      )}
                    </Field>
                  </>
                )
                : (
                  <Wide>
                    <Field label={DESCRIPTION} optional>
                      {({ id: fieldId }) => (
                        <TextInput id={fieldId} value={draft.description} disabled={!canEdit}
                          onChange={(event) => patch({ description: event.target.value })} />
                      )}
                    </Field>
                  </Wide>
                )}
            </FormGrid>

            <FormSection title={WHAT_IT_COSTS} sub={WHAT_IT_COSTS_SUB} />
            <PairField
              joiner="at"
              left={(
                <Field label={owned ? OWNED_BUY_LABEL : BUY_LABEL[kind]}>
                  {({ id: fieldId }) => (
                    <UnitSelect id={fieldId} label={owned ? OWNED_BUY_LABEL : BUY_LABEL[kind]}
                      units={lookups.units}
                      value={draft.purchaseUnitId ?? draft.unitId} disabled={!canEdit}
                      onChange={(value) => patch({ purchaseUnitId: value })}
                      onCreate={canEdit ? (name) => void newUnit(name, 'purchaseUnitId') : undefined} />
                  )}
                </Field>
              )}
              right={(
                <Field label={owned ? ownedCostPerLabel(buy.name) : costPerLabel(kind, buy.name)}>
                  {({ id: fieldId }) => (
                    <MoneyInput id={fieldId} value={draft.purchasePrice} disabled={!canEdit}
                      onChange={(event) => patch({ purchasePrice: event.target.value })} />
                  )}
                </Field>
              )}
            />
            <PairField
              joiner="="
              left={(
                <Field label={ALLOCATED_IN} required error={errors.unitId}>
                  {({ id: fieldId, invalid }) => (
                    <UnitSelect id={fieldId} label={ALLOCATED_IN} units={lookups.units}
                      value={draft.unitId} invalid={invalid} disabled={!canEdit}
                      onChange={(value) => patch({ unitId: value })}
                      onCreate={canEdit ? (name) => void newUnit(name, 'unitId') : undefined} />
                  )}
                </Field>
              )}
              right={(
                <Field label={perLabel(allocMany, buy.name)} error={errors.purchaseConversion}>
                  {({ id: fieldId, invalid }) => (
                    <NumberInput id={fieldId} invalid={invalid} unit={allocMany}
                      value={draft.purchaseConversion} disabled={!canEdit}
                      onChange={(event) => patch({ purchaseConversion: event.target.value })} />
                  )}
                </Field>
              )}
            />
            <Band tone="green" title={buyingSentence(rule)}>
              {owned ? OWNED_BAND_HINT : BAND_HINT[kind]}
            </Band>

            <FormGrid>
              <Field label={MINIMUM_LABEL[kind]}
                hint={owned ? OWNED_MINIMUM_HINT : MINIMUM_HINT[kind]}>
                {({ id: fieldId, describedBy }) => (
                  <NumberInput id={fieldId} describedBy={describedBy} unit={buyOne}
                    value={draft.minOrderQty} disabled={!canEdit}
                    onChange={(event) => patch({ minOrderQty: event.target.value })} />
                )}
              </Field>
            </FormGrid>

            <Field group label={ruleLabel(kind, buy.name)} hint={ruleHint(kind, buy.name)}>
              {({ labelId }) => (
                <SegmentedPicker
                  labelledBy={labelId}
                  label={ruleLabel(kind, buy.name)}
                  value={draft.costBasis}
                  onChange={(value) => patch({ costBasis: value })}
                  options={[
                    { value: 'used' as const, label: ruleUsed(kind, allocMany) },
                    { value: 'whole' as const, label: ruleWhole(buy.name) },
                  ]}
                />
              )}
            </Field>

            <TryItPanel rule={rule} sellsEach={sells} kind={kind === 'equipment' ? 'equipment' : 'fee'}
              qty={tryQty ?? autoQty} onQty={setTryQty} allocWord={allocMany} />

            <FormSection title={WHAT_IT_SELLS_FOR} />
            <PricedByField value={draft.priceMode} disabled={!canEdit}
              onChange={(mode) => patch({ priceMode: mode === 'company' ? 'markup' : mode })} />
            <FormGrid>
              {draft.priceMode === 'fixed'
                ? (
                  <Field label={pricePer(allocOne)}>
                    {({ id: fieldId }) => (
                      <MoneyInput id={fieldId} value={draft.price} disabled={!canEdit}
                        onChange={(event) => patch({ price: event.target.value })} />
                    )}
                  </Field>
                )
                : (
                  <Field label={MARKUP_ON_COST}>
                    {({ id: fieldId }) => (
                      <NumberInput id={fieldId} percent value={draft.markupPct} disabled={!canEdit}
                        onChange={(event) => patch({ markupPct: event.target.value })} />
                    )}
                  </Field>
                )}
            </FormGrid>
            <Readout items={[
              { label: costSlash(allocOne), value: formatUnitCost(cost) },
              { label: sellsSlash(allocOne), value: formatUnitCost(sells) },
              { label: 'Margin', value: percentText(margin), tone: marginTone(margin) },
            ]} />

            <WhereAndOptions available={available} orgUnitIds={draft.orgUnitIds}
              onOrgUnits={(next) => patch({ orgUnitIds: next })} taxable={draft.taxable}
              onTaxable={(next) => patch({ taxable: next })} disabled={!canEdit} />
            <NotesField value={draft.notes} disabled={!canEdit}
              onChange={(next) => patch({ notes: next })} />
          </div>
        )}
    </CatalogSheet>
  )
}
